import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { type ProfileAttributeValue } from '@/types';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    attributes?: Record<string, ProfileAttributeValue>;
    languages?: string[];
}

// ── Custom SVG icons (24×24, stroke-based) ─────────────────────────────────

function SvgHeight() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <line x1="12" y1="2" x2="12" y2="22" />
            <polyline points="8 6 12 2 16 6" />
            <polyline points="8 18 12 22 16 18" />
            <line x1="8" y1="8" x2="11" y2="8" />
            <line x1="8" y1="11" x2="13" y2="11" />
            <line x1="8" y1="14" x2="11" y2="14" />
            <line x1="8" y1="17" x2="13" y2="17" />
        </svg>
    );
}

function SvgWeight() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M12 3a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
            <path d="M12 3 L12 7" />
            <line x1="4" y1="7" x2="20" y2="7" />
            <path d="M4 7 L3 14 L7 14 Q6 11 4 7" />
            <path d="M20 7 L21 14 L17 14 Q18 11 20 7" />
            <line x1="3" y1="14" x2="7" y2="14" />
            <line x1="17" y1="14" x2="21" y2="14" />
            <path d="M6 20 Q12 17 18 20" />
        </svg>
    );
}

function SvgShoe() {
    return (
        <svg viewBox="0 0 122.41 122.88" fill="currentColor" className="h-5 w-5">
            <path fillRule="evenodd" clipRule="evenodd" d="M14.09,59.81L0,45.31v77.56l6.41,0V68.24C6.42,63.2,10.35,60.28,14.09,59.81L14.09,59.81L14.09,59.81z M0,39.47C0,22.01,8.7,5.01,20.78,3.83C38.72,9.08,53.36,135.99,95.89,91c8.15,6.26,15.64,13.56,21.93,22.75 c6.66,9.74,6.63,8.52-3.76,8.52H62.42C36.87,110.57,43.46,68.58,0,39.47L0,39.47L0,39.47z M23.11,2.67 c2.16-1.49,4.53-2.44,7.05-2.67c15.4,4.42,28.3,100.06,59.8,91.4C54.47,113.32,41.02,7.08,24.76,2.41 C24.21,2.46,23.65,2.55,23.11,2.67L23.11,2.67L23.11,2.67z" />
        </svg>
    );
}

function SvgEye() {
    return (
        <svg viewBox="0 0 122.88 68.18" fill="currentColor" className="h-5 w-5">
            <path d="M61.44,13.81a20.31,20.31,0,1,1-14.34,6,20.24,20.24,0,0,1,14.34-6ZM1.05,31.31A106.72,106.72,0,0,1,11.37,20.43C25.74,7.35,42.08.36,59,0s34.09,5.92,50.35,19.32a121.91,121.91,0,0,1,12.54,12,4,4,0,0,1,.25,5,79.88,79.88,0,0,1-15.38,16.41A69.53,69.53,0,0,1,63.43,68.18,76,76,0,0,1,19.17,53.82,89.35,89.35,0,0,1,.86,36.44a3.94,3.94,0,0,1,.19-5.13Zm15.63-5A99.4,99.4,0,0,0,9.09,34,80.86,80.86,0,0,0,23.71,47.37,68.26,68.26,0,0,0,63.4,60.3a61.69,61.69,0,0,0,38.41-13.72,70.84,70.84,0,0,0,12-12.3,110.45,110.45,0,0,0-9.5-8.86C89.56,13.26,74.08,7.58,59.11,7.89S29.63,14.48,16.68,26.27Zm39.69-7.79a7.87,7.87,0,1,1-7.87,7.87,7.86,7.86,0,0,1,7.87-7.87Z" />
        </svg>
    );
}

function SvgHair() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Head circle */}
            <circle cx="12" cy="7" r="4" />
            {/* Hair strands flowing down */}
            <path d="M8 9 Q5 14 6 20" />
            <path d="M10 11 Q9 16 10 21" />
            <path d="M14 11 Q15 16 14 21" />
            <path d="M16 9 Q19 14 18 20" />
        </svg>
    );
}

function SvgSkinTone() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Open hand/palm */}
            <path d="M8 14 L8 6 Q8 4.5 9.5 4.5 Q11 4.5 11 6 L11 12" />
            <path d="M11 11 L11 4 Q11 2.5 12.5 2.5 Q14 2.5 14 4 L14 11" />
            <path d="M14 11 L14 5 Q14 3.5 15.5 3.5 Q17 3.5 17 5 L17 12" />
            <path d="M17 12 L17 8 Q17 6.5 18.5 6.5 Q20 6.5 20 8 L20 15 Q20 20 15 21 L10 21 Q6 21 5 17 L4 15 Q3.5 13 5 12.5 Q6.5 12 7 13.5 L8 14" />
        </svg>
    );
}

function SvgBodyType() {
    return (
        <svg viewBox="0 0 122.88 116.37" fill="currentColor" className="h-5 w-5">
            <path d="M59.11,110.2c-0.44-0.18-0.77-0.53-0.94-0.95c-0.17-0.41-0.18-0.89,0-1.34c0.18-0.44,0.53-0.77,0.95-0.94 c0.41-0.17,0.89-0.18,1.34,0c0.18,0.07,0.36,0.13,0.53,0.17c0.17,0.04,0.34,0.06,0.5,0.07c0.17,0.01,0.34-0.01,0.52-0.03 c0.17-0.03,0.35-0.07,0.53-0.14c0.45-0.16,0.93-0.13,1.33,0.06c0.4,0.19,0.74,0.54,0.9,0.99l0.01,0.03 c0.15,0.44,0.11,0.91-0.08,1.3c-0.19,0.4-0.54,0.74-0.99,0.9l-0.03,0.01c-0.38,0.13-0.76,0.23-1.14,0.29 c-0.39,0.06-0.78,0.09-1.16,0.07c-0.39-0.01-0.77-0.06-1.15-0.15C59.84,110.47,59.47,110.35,59.11,110.2L59.11,110.2z M72.23,1.85 c-0.03-0.48,0.14-0.92,0.43-1.25l0.02-0.03C72.98,0.25,73.4,0.03,73.86,0c0.48-0.03,0.93,0.14,1.26,0.43l0.02,0.02 c0.32,0.29,0.53,0.71,0.56,1.18c0.06,0.96,0.22,1.86,0.47,2.69c0.25,0.81,0.59,1.55,1.04,2.23l0.03,0.04 c0.46,0.68,1.02,1.3,1.7,1.86l0.02,0.02c0.68,0.55,1.47,1.04,2.38,1.46c1.33,0.45,2.75,1.01,4.26,1.68 c1.5,0.67,3.09,1.45,4.78,2.35c1.66,0.89,3.41,1.89,5.25,3.02c1.83,1.12,3.76,2.36,5.78,3.72c1.73,0.48,3.34,1.07,4.84,1.78 c1.52,0.71,2.92,1.54,4.2,2.48c1.32,0.97,2.52,2.07,3.58,3.28c1.06,1.21,1.99,2.54,2.79,4l0.04,0.08c0.51,0.99,0.95,2.08,1.33,3.25 c0.4,1.22,0.72,2.52,0.99,3.9c0.27,1.38,0.47,2.84,0.62,4.35c0.15,1.55,0.25,3.16,0.28,4.78c0.7,2.46,1.25,4.98,1.66,7.55 c0.42,2.58,0.71,5.21,0.89,7.89c0.18,2.69,0.25,5.44,0.24,8.24c-0.01,2.81-0.11,5.65-0.27,8.55c-0.02,0.48-0.24,0.91-0.57,1.21 l-0.03,0.02c-0.33,0.29-0.76,0.45-1.23,0.43c-0.48-0.02-0.91-0.24-1.21-0.57l-0.02-0.03c-0.29-0.33-0.45-0.76-0.43-1.23 c0.16-2.84,0.25-5.63,0.26-8.35c0.01-2.72-0.06-5.4-0.23-8.03c-0.17-2.6-0.45-5.14-0.85-7.62c-0.4-2.47-0.93-4.89-1.61-7.26 c-0.03-0.08-0.05-0.16-0.06-0.24l0-0.02c-0.01-0.08-0.02-0.16-0.02-0.25c-0.03-1.57-0.12-3.13-0.26-4.63 c-0.14-1.43-0.33-2.82-0.59-4.14c-0.24-1.22-0.52-2.37-0.86-3.43c-0.32-1.01-0.7-1.94-1.13-2.77 c-0.68-1.23-1.46-2.36-2.35-3.38c-0.9-1.02-1.9-1.95-3.02-2.77c-1.13-0.83-2.37-1.56-3.72-2.18c-1.36-0.63-2.84-1.16-4.43-1.59 c-0.02-0.01-0.04-0.01-0.06-0.02c-0.07-0.02-0.14-0.05-0.21-0.08c-0.09-0.04-0.18-0.09-0.25-0.14l-0.02-0.02 c-2.01-1.36-3.93-2.6-5.76-3.72c-1.83-1.12-3.55-2.11-5.18-2.98c-1.6-0.85-3.11-1.6-4.53-2.23c-1.41-0.63-2.75-1.15-4-1.58 l-0.11-0.04c-0.04-0.02-0.09-0.03-0.12-0.05c-1.25-0.57-2.34-1.24-3.27-2c-0.94-0.77-1.72-1.64-2.36-2.6 c-0.63-0.95-1.11-1.98-1.45-3.08c-0.35-1.11-0.56-2.3-0.64-3.56L72.23,1.85L72.23,1.85z M47.17,1.63 c0.03-0.48,0.25-0.9,0.59-1.19c0.33-0.3,0.78-0.46,1.26-0.43c0.48,0.03,0.91,0.25,1.2,0.59c0.29,0.33,0.46,0.78,0.43,1.26 c-0.08,1.26-0.29,2.45-0.64,3.56c-0.34,1.1-0.82,2.13-1.45,3.07l-0.03,0.05l-0.02,0.03c-0.64,0.95-1.41,1.81-2.34,2.57 c-0.94,0.77-2.02,1.44-3.27,2c-0.04,0.02-0.08,0.03-0.12,0.05l-0.11,0.04c-1.26,0.43-2.59,0.95-4,1.58 c-1.42,0.63-2.93,1.37-4.53,2.23c-1.63,0.87-3.35,1.86-5.18,2.98c-1.83,1.12-3.76,2.37-5.78,3.74 c-0.08,0.05-0.16,0.1-0.25,0.14c-0.08,0.03-0.15,0.06-0.22,0.09L22.64,24c-1.59,0.43-3.07,0.96-4.43,1.59 c-1.35,0.63-2.6,1.35-3.72,2.18c-1.12,0.82-2.12,1.74-3.02,2.77c-0.89,1.02-1.68,2.15-2.35,3.38l-0.01,0.01 c-0.43,0.83-0.8,1.76-1.12,2.77c-0.34,1.06-0.63,2.21-0.86,3.43c-0.26,1.32-0.45,2.71-0.59,4.14c-0.15,1.5-0.23,3.05-0.26,4.63 c0,0.09-0.01,0.18-0.02,0.26c-0.01,0.08-0.03,0.16-0.06,0.24c-0.68,2.36-1.21,4.78-1.61,7.26c-0.4,2.48-0.68,5.02-0.85,7.62 c-0.18,2.63-0.25,5.31-0.23,8.03c0.01,2.72,0.11,5.51,0.26,8.35c0.02,0.48-0.15,0.93-0.45,1.26c-0.3,0.33-0.72,0.55-1.21,0.57 c-0.48,0.02-0.93-0.15-1.26-0.45c-0.33-0.3-0.55-0.72-0.57-1.21C0.11,77.93,0.01,75.08,0,72.28c-0.01-2.81,0.06-5.56,0.24-8.24 c0.18-2.68,0.47-5.31,0.89-7.89c0.42-2.57,0.96-5.09,1.66-7.55c0.04-1.63,0.13-3.23,0.28-4.78c0.15-1.51,0.35-2.97,0.62-4.35 c0.27-1.37,0.59-2.68,0.99-3.9c0.38-1.17,0.82-2.26,1.33-3.25l0.04-0.08c0.8-1.45,1.73-2.78,2.79-4 c1.06-1.22,2.26-2.31,3.58-3.28c1.28-0.94,2.68-1.76,4.2-2.48c1.5-0.7,3.11-1.3,4.84-1.78c2.02-1.36,3.94-2.6,5.78-3.72 c1.84-1.12,3.59-2.13,5.25-3.02c1.68-0.9,3.27-1.68,4.78-2.35c1.51-0.67,2.92-1.23,4.26-1.68c0.92-0.42,1.72-0.91,2.4-1.47 c0.68-0.56,1.24-1.18,1.7-1.86l0.03-0.04c0.45-0.68,0.79-1.42,1.04-2.23c0.26-0.83,0.41-1.73,0.47-2.69L47.17,1.63L47.17,1.63z" />
        </svg>
    );
}

function SvgBreast() {
    return (
        <svg viewBox="0 0 123.1 104.65" fill="currentColor" className="h-5 w-5">
            <path fillRule="evenodd" clipRule="evenodd" d="M94.72,40.87c-3.81,0-6.9,3.09-6.9,6.9s3.09,6.9,6.9,6.9c3.81,0,6.9-3.09,6.9-6.9S98.53,40.87,94.72,40.87 L94.72,40.87z M28.38,40.87c3.81,0,6.9,3.09,6.9,6.9s-3.09,6.9-6.9,6.9c-3.81,0-6.9-3.09-6.9-6.9S24.56,40.87,28.38,40.87 L28.38,40.87z M12.1,0.34l0.53,7.99c0.06,0.9,0.13,1.57,0.2,2.13c0.17,1.53,0.21,1.88-1.13,3.95C-1.79,35.26-2.12,51.2,3.68,61.75 c3.47,6.31,9.12,10.66,15.46,12.98c0.17,0.06,0.34,0.12,0.52,0.18c0.02,5.05-0.23,10.06-0.81,15c-0.59,5-1.53,9.89-2.91,14.63h3.39 c1.44-4.98,2.42-9.07,3.03-14.22c0.56-4.71,0.81-9.52,0.83-14.42c5.17,1.15,10.6,1.02,15.54-0.41 c9.43-2.73,17.14-10.16,18.22-22.53l-3.53-0.3c-0.94,10.69-7.57,17.09-15.68,19.45c-5.55,1.61-11.78,1.35-17.4-0.7 c-5.58-2.04-10.54-5.85-13.57-11.36c-5.23-9.51-4.77-24.17,7.88-43.71c2.03-3.14,1.97-3.71,1.68-6.26 c-0.06-0.53-0.13-1.17-0.18-1.97l-0.53-7.99L12.1,0.34L12.1,0.34z M28.38,45.59c1.21,0,2.18,0.98,2.18,2.18 c0,1.21-0.98,2.18-2.18,2.18c-1.21,0-2.18-0.98-2.18-2.18C26.19,46.57,27.17,45.59,28.38,45.59L28.38,45.59z M110.99,0.34 l-0.53,7.99c-0.06,0.9-0.13,1.57-0.2,2.13c-0.17,1.53-0.21,1.88,1.13,3.95c13.49,20.85,13.82,36.79,8.02,47.34 c-3.47,6.31-9.12,10.66-15.46,12.98c-0.17,0.06-0.34,0.12-0.52,0.18c-0.02,5.05,0.23,10.06,0.81,15c0.59,5,1.53,9.89,2.91,14.63 h-3.39c-1.44-4.98-2.42-9.07-3.03-14.22c-0.56-4.71-0.81-9.52-0.83-14.42c-5.17,1.15-10.6,1.02-15.54-0.41 c-9.43-2.73-17.14-10.16-18.22-22.53l3.53-0.3c0.94,10.69,7.57,17.09,15.68,19.45c5.55,1.61,11.78,1.35,17.4-0.7 c5.58-2.04,10.54-5.85,13.57-11.36c5.23-9.51,4.77-24.17-7.88-43.71c-2.03-3.14-1.97-3.71-1.68-6.26c0.06-0.53,0.13-1.17,0.18-1.97 l0.53-7.99L110.99,0.34L110.99,0.34z M94.72,45.59c-1.21,0-2.18,0.98-2.18,2.18c0,1.21,0.98,2.18,2.18,2.18 c1.21,0,2.18-0.98,2.18-2.18C96.91,46.57,95.93,45.59,94.72,45.59L94.72,45.59z" />
        </svg>
    );
}

function SvgHips() {
    return (
        <svg viewBox="0 0 118.04 122.88" fill="currentColor" className="h-5 w-5">
            <path d="M69.36,68.39c-0.7,3.09-1.21,6.42-1.58,9.94c-0.39,3.77-0.6,7.77-0.67,11.95c-0.08,4.63,0,9.51,0.21,14.57 c0.2,5.04,0.53,10.27,0.92,15.64c0.04,0.61-0.17,1.19-0.54,1.62c-0.38,0.43-0.91,0.72-1.53,0.76c-0.61,0.04-1.19-0.17-1.62-0.54 c-0.43-0.38-0.72-0.91-0.76-1.53c-0.39-5.36-0.71-10.63-0.92-15.76c-0.2-5.12-0.29-10.08-0.2-14.83c0.08-4.63,0.33-9.03,0.78-13.17 c0.44-4.03,1.08-7.82,1.96-11.3c-1.56-1.43-2.9-3.24-4.03-5.4c-0.93-1.77-1.71-3.78-2.35-6.03c-0.64,2.25-1.42,4.26-2.35,6.03 c-1.13,2.16-2.47,3.97-4.03,5.4c0.88,3.48,1.52,7.27,1.96,11.3c0.45,4.13,0.7,8.54,0.78,13.17c0.09,4.75,0,9.71-0.2,14.83 c-0.2,5.13-0.53,10.4-0.92,15.76c-0.04,0.61-0.33,1.15-0.76,1.53c-0.43,0.37-1.01,0.58-1.62,0.54c-0.61-0.04-1.15-0.33-1.53-0.76 c-0.37-0.43-0.58-1-0.54-1.62c0.39-5.36,0.71-10.6,0.92-15.64c0.2-5.06,0.29-9.94,0.21-14.57c-0.08-4.18-0.29-8.18-0.68-11.95 c-0.36-3.52-0.88-6.85-1.58-9.94c-3.2,1.47-6.97,1.83-11.31,1.05c-4.55-0.82-9.74-2.9-15.59-6.28c-0.51-0.31-0.86-0.8-1-1.34 c-0.14-0.54-0.09-1.13,0.2-1.65c0.31-0.51,0.8-0.86,1.34-1c0.55-0.15,1.16-0.08,1.69,0.23c5.72,3.31,10.63,5.25,14.76,5.88 c4.06,0.62,7.36-0.03,9.92-1.91c2.65-1.94,4.64-5.2,5.98-9.74c1.37-4.6,2.08-10.5,2.15-17.63c0.08-0.54,0.3-1.02,0.64-1.38 c0.2-0.2,0.38-0.35,0.64-0.47c0.76-0.37,1.73-0.28,2.38,0.27c0.18,0.15,0.37,0.57,0.45,0.79c0.05,0.18,0.07,0.37,0.08,0.57 c0.07,7.13,0.78,13.03,2.15,17.63c1.35,4.54,3.33,7.8,5.98,9.74c2.56,1.88,5.86,2.53,9.92,1.91c4.13-0.63,9.05-2.58,14.76-5.88 c0.53-0.31,1.14-0.37,1.69-0.23c0.55,0.15,1.05,0.5,1.36,1.04c0.3,0.53,0.37,1.13,0.22,1.68c-0.15,0.55-0.5,1.05-1.04,1.36 c-5.86,3.39-11.06,5.47-15.62,6.3C76.33,70.22,72.57,69.86,69.36,68.39L69.36,68.39z M15.46,1.13c0.3-0.54,0.79-0.9,1.34-1.05 c0.55-0.15,1.15-0.1,1.69,0.2c0.54,0.3,0.9,0.79,1.05,1.34c0.15,0.55,0.1,1.15-0.2,1.69C14.43,12.01,10.72,20.88,8.22,30 c-2.49,9.12-3.77,18.48-3.77,28.08c-0.01,9.62,1.26,19.5,3.84,29.65c2.58,10.16,6.48,20.58,11.73,31.27 c0.27,0.55,0.29,1.16,0.1,1.69c-0.18,0.54-0.58,1.01-1.13,1.28c-0.55,0.26-1.15,0.28-1.69,0.1c-0.54-0.18-1.01-0.58-1.28-1.13 c-5.4-11-9.41-21.73-12.06-32.2C1.3,78.27-0.01,68.05,0,58.08c0.01-10,1.33-19.73,3.92-29.22C6.51,19.37,10.37,10.13,15.46,1.13 L15.46,1.13z M98.71,3.32c-0.3-0.53-0.36-1.14-0.2-1.69c0.15-0.55,0.52-1.04,1.05-1.34c0.53-0.3,1.14-0.36,1.69-0.2 c0.55,0.15,1.04,0.52,1.34,1.05c5.08,8.99,8.93,18.21,11.52,27.68c2.59,9.49,3.91,19.23,3.92,29.22 c0.01,9.98-1.3,20.2-3.95,30.67c-2.65,10.47-6.66,21.2-12.07,32.2c-0.27,0.55-0.74,0.94-1.28,1.13 c-0.52,0.18-1.11,0.16-1.65-0.08c-0.53-0.27-0.9-0.73-1.08-1.25c-0.18-0.52-0.16-1.11,0.08-1.65 c5.24-10.67,9.13-21.08,11.71-31.22c2.58-10.15,3.84-20.03,3.84-29.65c-0.01-9.6-1.28-18.96-3.77-28.08 C107.32,20.87,103.61,11.98,98.71,3.32L98.71,3.32z" />
        </svg>
    );
}

function SvgEthnicity() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <circle cx="12" cy="12" r="9" />
            {/* Meridian */}
            <path d="M12 3 Q16 8 16 12 Q16 16 12 21" />
            <path d="M12 3 Q8 8 8 12 Q8 16 12 21" />
            {/* Parallels */}
            <line x1="3.5" y1="9" x2="20.5" y2="9" />
            <line x1="3.5" y1="15" x2="20.5" y2="15" />
        </svg>
    );
}

function SvgPubicHair() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Triangle base */}
            <path d="M5 20 L12 6 L19 20 Z" />
            {/* Hair strands inside */}
            <path d="M10 17 Q10 13 11 11" strokeWidth="1.25" />
            <path d="M12 18 Q12 14 12 12" strokeWidth="1.25" />
            <path d="M14 17 Q14 13 13 11" strokeWidth="1.25" />
        </svg>
    );
}

function SvgTattoo() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Compass star */}
            <path d="M12 2 L13.2 10.8 L22 12 L13.2 13.2 L12 22 L10.8 13.2 L2 12 L10.8 10.8 Z" />
            {/* Inner diamond */}
            <path d="M12 8 L14 12 L12 16 L10 12 Z" strokeWidth="1" />
        </svg>
    );
}

function SvgPiercing() {
    return (
        <svg viewBox="0 0 58.04 122.88" fill="currentColor" className="h-5 w-5">
            <path d="M4.04,122.88H0V46.75c0-4.96,0.01,0.31,0.01-1.46c-0.02-9.94-0.03-17.92,7.16-26.72c0.13-0.16,0.27-0.29,0.43-0.4v-2.86 c0-7.46,5.64-12.17,12.88-14.19c2.7-0.75,5.64-1.13,8.58-1.12c2.95,0.01,5.89,0.39,8.61,1.15c7.38,2.07,13.17,6.88,13.17,14.45 v2.93c0.05,0.05,0.1,0.1,0.14,0.15c7.16,8.61,7.12,16.3,7.06,25.91c0,0.07,0,0.1,0,2.45v75.84h-4.04V47.04 c0-0.68,0.01-0.92,0.02-2.46c0.04-7.1,0.07-13.05-3.18-19.02V26c0,15.53-5.49,25.4-12.53,29.77c-2.84,1.76-5.94,2.64-9.05,2.64 c-3.11,0-6.2-0.88-9.04-2.62C13.13,51.45,7.59,41.71,7.59,26.83v-1.81C4,31.35,4.01,37.7,4.03,45.29c0,2.74,0.01-2.51,0.01,1.46 V122.88L4.04,122.88z M47.29,26V15.6c0-5.69-4.65-9.39-10.59-11.06c-2.42-0.68-5.04-1.02-7.65-1.03 c-2.62-0.01-5.23,0.33-7.64,1c-5.79,1.61-10.29,5.22-10.29,10.79v11.52c0,13.5,4.79,22.19,10.92,25.95 c2.29,1.4,4.75,2.11,7.2,2.11c2.45,0,4.91-0.7,7.18-2.11C42.53,48.99,47.29,40.15,47.29,26L47.29,26z M17.48,96.71 c-0.92-0.31-1.42-1.31-1.11-2.23c0.31-0.92,1.31-1.42,2.23-1.11c4.7,1.58,7.59,2.28,10.4,2.24c2.8-0.04,5.74-0.84,10.49-2.26 c0.93-0.28,1.92,0.25,2.19,1.19c0.28,0.93-0.25,1.92-1.19,2.19c-5.03,1.5-8.17,2.35-11.46,2.4 C25.76,99.18,22.56,98.42,17.48,96.71L17.48,96.71z M13.79,86.47c-0.92-0.31-1.42-1.31-1.11-2.23 c0.31-0.92,1.31-1.42,2.23-1.11c4.7,1.58,9.4,2.28,14.1,2.24c4.71-0.05,9.43-0.84,14.18-2.26c0.93-0.28,1.92,0.25,2.19,1.19 s-0.25,1.92-1.19,2.19c-5.03,1.5-10.09,2.35-15.16,2.4C23.95,88.94,18.86,88.18,13.79,86.47L13.79,86.47z" />
        </svg>
    );
}

function SvgLanguage() {
    return (
        <svg viewBox="0 0 122.88 69.27" fill="currentColor" className="h-5 w-5">
            <path d="M61.86,26.32c14.25-5.59,26.43-1.02,37.55,8.84c-25.23,10.75-50.46,10.64-75.69,0 C34.81,25.67,46.97,20.48,61.86,26.32L61.86,26.32z M61.89,7.67c11.44-8.97,22.16-9.54,32.34-3.66 c10.12,5.85,19.51,18.25,28.4,35.12c0.48,0.91,0.26,2.01-0.47,2.67c-20.16,18.41-40.4,27.51-60.72,27.46 c-20.31-0.05-40.55-9.26-60.71-27.46c-0.77-0.7-0.94-1.82-0.46-2.7c8.85-16.25,18.2-28.85,28.42-34.96 C38.97-1.99,49.94-1.72,61.89,7.67L61.89,7.67z M92.01,7.85c-8.87-5.12-18.37-4.29-28.69,4.38 c-0.81,0.68-2.01,0.7-2.84,0c-10.8-9.08-20.53-9.63-29.51-4.26C21.81,13.42,13.24,24.84,5,39.69 C23.88,56.37,42.7,64.8,61.44,64.85c18.75,0.05,37.57-8.3,56.46-25.17C109.63,24.29,101.05,13.07,92.01,7.85L92.01,7.85z" />
        </svg>
    );
}

function SvgShoulders() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Head */}
            <circle cx="12" cy="5" r="2.5" />
            {/* Shoulder line */}
            <path d="M5 9 Q8 8 12 8 Q16 8 19 9" />
            {/* Arms drop */}
            <path d="M5 9 L4 16" />
            <path d="M19 9 L20 16" />
            {/* Torso */}
            <path d="M9 8.5 L9 16 Q12 17 15 16 L15 8.5" />
        </svg>
    );
}

function SvgMale() {
    return (
        <svg viewBox="0 0 111.29 122.88" fill="currentColor" className="h-5 w-5">
            <path d="M111.29,120.87c0,1.02-0.84,1.85-1.86,1.85c-1.02,0-1.85-0.84-1.85-1.86c0.21-36.81-4.44-58.75-8.24-75.03 c-3.39-14.54-6.11-26.2-4.19-44.16c0.11-1.02,1.02-1.76,2.04-1.65c1.02,0.11,1.76,1.02,1.65,2.04 c-1.84,17.33,0.81,28.72,4.12,42.93C106.8,61.44,111.5,83.59,111.29,120.87L111.29,120.87z M69.34,68.32 c0.42-0.94,1.52-1.36,2.45-0.94c0.94,0.42,1.36,1.52,0.94,2.45c-0.34,0.75-1.01,1.57-1.81,2.56c-1.16,1.42-2.67,3.27-3.32,5.1 c-2.15,6.1-4.06,10.73-5.9,13.88c0,0.07,0,0.15-0.01,0.22c-0.5,4.34-0.74,9.12-0.81,14.07c-0.07,4.99,0.03,10.19,0.22,15.28 c0.04,1.02-0.76,1.88-1.79,1.92c-1.02,0.04-1.88-0.76-1.92-1.79c-0.19-5.12-0.29-10.37-0.22-15.46c0.05-3.35,0.17-6.61,0.39-9.69 c-0.49,0.24-0.99,0.37-1.49,0.4c-0.66,0.04-1.3-0.09-1.93-0.38c0.25,2.76,0.42,5.72,0.53,8.77c0.19,5.44,0.16,11.11-0.06,16.27 c-0.04,1.02-0.9,1.82-1.93,1.78c-1.02-0.04-1.82-0.9-1.78-1.93c0.21-5.09,0.25-10.67,0.06-16.01c-0.17-4.86-0.52-9.53-1.08-13.46 l-0.01-0.04c-1.8-3.01-3.67-7.4-5.76-13.17c-0.87-2.39-2.77-4.43-4.02-5.78c-0.61-0.66-1.09-1.18-1.39-1.65 c-0.54-0.87-0.27-2.02,0.6-2.56c0.87-0.54,2.02-0.27,2.56,0.6c0.11,0.18,0.48,0.58,0.96,1.08c1.45,1.56,3.66,3.94,4.79,7.04 c2.18,6.02,3.93,10.28,5.37,12.84c0.26,0.24,0.46,0.55,0.55,0.91c0.88,1.36,1.65,2.02,2.34,1.98c1.98-0.12,4.47-5.66,8.26-16.37 c0.86-2.44,2.6-4.57,3.94-6.21C68.68,69.26,69.21,68.61,69.34,68.32L69.34,68.32z M51.01,10.97c-0.84-0.58-1.06-1.74-0.47-2.58 c0.58-0.84,1.74-1.06,2.58-0.47c0.85,0.59,1.66,0.89,2.41,0.83c0.74-0.06,1.53-0.48,2.38-1.35c0.71-0.74,1.89-0.75,2.63-0.04 c0.74,0.72,0.75,1.89,0.04,2.63c-1.52,1.56-3.12,2.35-4.77,2.47C54.15,12.58,52.56,12.05,51.01,10.97L51.01,10.97z M3.72,120.85 c0,1.02-0.82,1.86-1.85,1.86c-1.02,0-1.86-0.82-1.86-1.85c-0.22-37.28,4.49-59.43,8.32-75.89c3.31-14.21,5.97-25.6,4.12-42.93 c-0.11-1.02,0.63-1.94,1.65-2.04c1.02-0.11,1.94,0.63,2.04,1.65c1.91,17.96-0.8,29.62-4.19,44.16 C8.16,62.11,3.5,84.05,3.72,120.85L3.72,120.85z" />
        </svg>
    );
}

function SvgGenital() {
    return (
        <svg viewBox="0 0 119.59 122.88" fill="currentColor" className="h-5 w-5">
            <path d="M53.34,9.46c-0.3-0.55-0.36-1.16-0.21-1.72c0.15-0.57,0.52-1.09,1.08-1.41c0.55-0.32,1.18-0.38,1.75-0.23 c0.57,0.15,1.09,0.52,1.41,1.08c0.22,0.38,0.45,0.7,0.7,0.95c0.23,0.23,0.48,0.4,0.74,0.5c0.26,0.1,0.57,0.14,0.92,0.11 c0.38-0.03,0.81-0.13,1.3-0.31c0.6-0.22,1.23-0.18,1.77,0.06c0.54,0.25,0.98,0.7,1.21,1.3c0.22,0.6,0.18,1.23-0.06,1.77 c-0.25,0.54-0.7,0.98-1.3,1.21c-1.03,0.38-2,0.58-2.91,0.61c-0.92,0.03-1.78-0.12-2.58-0.42c-0.8-0.31-1.52-0.76-2.15-1.35 c-0.62-0.57-1.16-1.27-1.63-2.08L53.34,9.46z M67.75,80.18l-0.05,0.22c-0.03,0.1-0.06,0.21-0.1,0.31 c-0.92,2.25-1.65,4.78-2.18,7.54c-0.56,2.89-0.92,6.02-1.09,9.32c-0.18,3.48-0.15,7.13,0.07,10.88 c0.22,3.85,0.65,7.81,1.25,11.77c0.09,0.63-0.08,1.24-0.43,1.72c-0.35,0.48-0.89,0.82-1.52,0.92 c-0.63,0.09-1.23-0.08-1.7-0.43c-0.48-0.35-0.82-0.89-0.92-1.52c-0.41-2.68-0.73-5.36-0.98-8.03 c-0.2,2.61-0.49,5.26-0.87,7.91c-0.09,0.63-0.43,1.17-0.9,1.53c-0.47,0.35-1.06,0.53-1.69,0.45l-0.07-0.01 c-0.62-0.1-1.14-0.43-1.49-0.9c-0.35-0.47-0.53-1.06-0.45-1.69c0.54-3.76,0.89-7.49,1.04-11.12 c0.16-3.67,0.12-7.24-0.12-10.65c-0.22-3.25-0.62-6.34-1.2-9.2c-0.56-2.78-1.28-5.35-2.17-7.65 c-0.4-0.47-0.87-1-1.37-1.57c-0.54-0.62-1.1-1.24-1.65-1.85c-1.07-1.19-2.15-2.36-3.16-3.44 c-1.18-1.27-2.27-2.42-3.13-3.32c-0.43-0.46-0.63-1.04-0.62-1.62c0.01-0.59,0.25-1.18,0.7-1.62 c0.46-0.43,1.04-0.63,1.63-0.62c0.59,0.01,1.18,0.25,1.62,0.71c0.92,0.96,2.05,2.15,3.24,3.43 c1.04,1.12,2.12,2.3,3.16,3.44c0.55,0.61,1.08,1.2,1.58,1.77c0.44,0.5,0.9,1.03,1.36,1.57 c0.13,0.1,0.25,0.21,0.35,0.34c0.1,0.12,0.19,0.25,0.27,0.4l0.52,0.62l0.5,0.61c0.44,0.49,0.85,0.86,1.23,1.09 c0.35,0.21,0.66,0.32,0.95,0.32c0.29,0,0.6-0.11,0.95-0.32c0.38-0.23,0.79-0.59,1.22-1.06 c0.69-0.83,1.48-1.74,2.31-2.69c0.94-1.06,1.94-2.16,2.93-3.24c1.13-1.23,2.3-2.47,3.41-3.64 c1.18-1.24,2.3-2.4,3.24-3.34c0.45-0.45,1.04-0.68,1.64-0.68c0.59,0,1.19,0.22,1.64,0.67 c0.43,0.45,0.65,1.03,0.66,1.61c0,0.59-0.22,1.19-0.67,1.64c-0.89,0.9-1.97,2.01-3.13,3.23 c-1.08,1.13-2.23,2.36-3.38,3.6l-1.32,1.44L67.75,80.18z M115.46,118.92c-0.14,0.61-0.5,1.11-0.99,1.43 c-0.49,0.32-1.09,0.45-1.7,0.34c-0.61-0.14-1.11-0.5-1.43-0.99c-0.32-0.5-0.46-1.12-0.33-1.74 c9.07-43.51,0.91-63.33-6.12-80.42c-2.28-5.53-4.44-10.79-5.92-16.46c-1.48-5.69-2.27-11.78-1.78-18.95 c0.05-0.63,0.34-1.19,0.79-1.58c0.44-0.38,1.02-0.6,1.65-0.57c0.62,0.05,1.17,0.35,1.56,0.79 c0.38,0.44,0.6,1.02,0.57,1.65c-0.45,6.62,0.3,12.3,1.7,17.63c1.41,5.36,3.48,10.39,5.66,15.68 C116.36,53.51,124.81,74.04,115.46,118.92z M8.69,118c0.12,0.62-0.02,1.23-0.34,1.72c-0.33,0.49-0.84,0.86-1.47,0.99 c-0.63,0.13-1.25-0.01-1.74-0.34c-0.49-0.33-0.86-0.84-0.99-1.47c-4.69-22.64-4.92-39.07-3.07-52.04 c1.86-12.99,5.8-22.49,9.44-31.28c2.17-5.25,4.24-10.23,5.64-15.55c1.4-5.3,2.15-10.96,1.7-17.57 c-0.04-0.63,0.18-1.22,0.57-1.67c0.39-0.45,0.95-0.75,1.59-0.79c0.64-0.04,1.23,0.18,1.68,0.57 c0.45,0.39,0.74,0.95,0.79,1.59c0.49,7.16-0.3,13.21-1.78,18.86c-1.47,5.63-3.63,10.83-5.9,16.31 c-7.04,16.97-15.2,36.71-6.11,80.59L8.69,118z" />
        </svg>
    );
}

function SvgHormone() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            {/* Flask/test tube */}
            <path d="M9 3 L9 12 L4 20 Q3.5 21 4.5 21 L19.5 21 Q20.5 21 20 20 L15 12 L15 3" />
            <line x1="8" y1="3" x2="16" y2="3" />
            {/* Liquid level */}
            <path d="M6 17 Q12 15 18 17" />
        </svg>
    );
}

function SvgBreastPresence() {
    return <SvgBreast />;
}

// ── Data helpers ───────────────────────────────────────────────────────────

const STAT_KEYS = ['height', 'weight', 'shoe_size'] as const;
type StatKey = typeof STAT_KEYS[number];

const STAT_META: Record<StatKey, { icon: () => React.ReactElement; abbr: string }> = {
    height:    { icon: SvgHeight, abbr: 'Height' },
    weight:    { icon: SvgWeight, abbr: 'Weight' },
    shoe_size: { icon: SvgShoe,   abbr: 'Shoe'   },
};

type ChipIconFn = () => React.ReactElement;

const CHIP_ICONS: Record<string, ChipIconFn> = {
    eye_color:             SvgEye,
    hair_color:            SvgHair,
    hair_length:           SvgHair,
    skin_tone:             SvgSkinTone,
    body_type:             SvgBodyType,
    breast_size:           SvgBreast,
    breast_type:           SvgBreast,
    breast_presence:       SvgBreastPresence,
    hips:                  SvgHips,
    shoulders:             SvgShoulders,
    ethnicity:             SvgEthnicity,
    pubic_hair:            SvgPubicHair,
    tattoos:               SvgTattoo,
    piercings:             SvgPiercing,
    penis_size:            SvgMale,
    penis_circumcision:    SvgMale,
    testicle_size:         SvgMale,
    genital_configuration: SvgGenital,
    hormone_therapy:       SvgHormone,
};

const APPEARANCE_KEYS = ['eye_color', 'hair_color', 'hair_length', 'skin_tone'];
const FIGURE_KEYS     = ['body_type', 'breast_size', 'breast_type', 'breast_presence', 'hips', 'shoulders'];
const INTIMATE_KEYS   = ['penis_size', 'penis_circumcision', 'testicle_size', 'genital_configuration', 'hormone_therapy'];
const PERSONAL_KEYS   = ['ethnicity', 'pubic_hair', 'tattoos', 'piercings', 'facial_features', 'distinctive_marks'];

// Breast attrs that get merged into the combined chip
const BREAST_MERGED = new Set(['breast_band', 'breast_cup', 'breast_size', 'breast_type', 'breast_presence']);

const EYE_COLORS: Record<string, string> = {
    blue:  '#6ba3e0',
    brown: '#8B5A2B',
    green: '#4a8c5c',
    hazel: '#7d6133',
    gray:  '#8e9399',
};
const HAIR_COLORS: Record<string, string> = {
    blonde:   '#e8d080',
    brunette: '#6b3a28',
    black:    '#1a1a1a',
    red:      '#c0392b',
    auburn:   '#8B3A27',
    gray:     '#9e9e9e',
};
const SKIN_TONES: Record<string, string> = {
    pale:  '#f5e6da',
    fair:  '#e8c9a8',
    olive: '#c9a87c',
    tan:   '#b5813d',
    brown: '#7d4e1e',
    dark:  '#3d2008',
};

function resolveColorDot(key: string, rawValue: string): string | null {
    if (key === 'eye_color')  return EYE_COLORS[rawValue]  ?? null;
    if (key === 'hair_color') return HAIR_COLORS[rawValue] ?? null;
    if (key === 'skin_tone')  return SKIN_TONES[rawValue]  ?? null;
    return null;
}

// ── Sub-components ─────────────────────────────────────────────────────────

function StatStrip({ stats }: { stats: Array<{ key: string; attr: ProfileAttributeValue }> }) {
    if (!stats.length) return null;
    return (
        <div className="flex gap-2">
            {stats.map(({ key, attr }) => {
                const meta = STAT_META[key as StatKey];
                const Icon = meta?.icon;
                return (
                    <div key={key} className="flex flex-1 flex-col items-center gap-1.5 rounded-xl border border-border bg-muted/20 py-4 px-2">
                        {Icon && <span className="text-muted-foreground"><Icon /></span>}
                        <span className="text-base font-bold tabular-nums leading-none">
                            {String(attr.value)}
                            {attr.suffix && (
                                <span className="ml-0.5 text-[11px] font-normal text-muted-foreground">
                                    {attr.suffix}
                                </span>
                            )}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {meta?.abbr ?? attr.label}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/** Single chip for any attribute */
function AttrChip({ attrKey, attr }: { attrKey: string; attr: ProfileAttributeValue }) {
    const Icon = CHIP_ICONS[attrKey];
    const rawVal = String(attr.value);
    const isBool = rawVal === 'true' || rawVal === 'false';
    const displayVal = isBool
        ? (rawVal === 'true' ? 'Yes' : 'No')
        : String(attr.value) + (attr.suffix ? ` ${attr.suffix}` : '');
    const colorDot = resolveColorDot(attrKey, rawVal);

    return (
        <div className="flex items-center gap-2.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
            {colorDot ? (
                <span
                    className="h-4 w-4 shrink-0 rounded-full border border-white/20"
                    style={{ backgroundColor: colorDot }}
                />
            ) : Icon ? (
                <span className="shrink-0 text-muted-foreground"><Icon /></span>
            ) : null}
            <div className="min-w-0 flex-1">
                <p className="truncate text-[10px] uppercase tracking-wide text-muted-foreground leading-none mb-1">
                    {attr.label}
                </p>
                <p className="truncate text-sm font-semibold leading-none">{displayVal}</p>
            </div>
        </div>
    );
}

/**
 * Combined breast chip.
 * Priority: band+cup ("90B") > cup only ("B") > breast_size ("Small (A-B)")
 * Secondary: breast_type shown after a · separator
 * Trans only: breast_presence shown standalone when no other breast data
 */
function BreastChip({ attrs }: { attrs: Record<string, ProfileAttributeValue> }) {
    const band    = attrs['breast_band'];
    const cup     = attrs['breast_cup'];
    const size    = attrs['breast_size'];
    const type    = attrs['breast_type'];
    const presence = attrs['breast_presence'];

    // No breast data at all
    if (!band && !cup && !size && !type && !presence) return null;

    // Presence-only (trans, no other data)
    if (presence && !band && !cup && !size && !type) {
        return (
            <div className="col-span-2 flex items-center gap-2.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
                <span className="shrink-0 text-muted-foreground"><SvgBreastPresence /></span>
                <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none mb-1">
                        {presence.label}
                    </p>
                    <p className="text-sm font-semibold leading-none">{String(presence.value)}</p>
                </div>
            </div>
        );
    }

    // Build primary bra size label
    let sizeLabel = '';
    if (band && cup) {
        sizeLabel = `${String(band.value)}${String(cup.value)}`;
    } else if (cup) {
        sizeLabel = `${String(cup.value)}`;
    } else if (size) {
        sizeLabel = String(size.value);
    }

    const typeLabel = type ? String(type.value) : '';
    const presenceLabel = presence ? String(presence.value) : '';

    const secondary = [typeLabel, presenceLabel].filter(Boolean).join(' · ');
    const displayVal = secondary ? `${sizeLabel} · ${secondary}` : sizeLabel;

    // Heading label
    const heading = (band && cup) ? 'Bra Size' : (cup ? 'Cup Size' : (size?.label ?? 'Chest'));

    return (
        <div className="col-span-2 flex items-center gap-2.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5">
            <span className="shrink-0 text-muted-foreground"><SvgBreast /></span>
            <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none mb-1">
                    {heading}
                </p>
                <p className="text-sm font-semibold leading-none">{displayVal}</p>
            </div>
        </div>
    );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2">
            {children}
        </p>
    );
}

function ChipGrid({ children }: { children: React.ReactNode }) {
    return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function LanguageChips({ languages }: { languages: string[] }) {
    return (
        <div>
            <SectionHeading>Languages</SectionHeading>
            <div className="flex flex-wrap gap-1.5">
                {languages.map((lang) => (
                    <span
                        key={lang}
                        className="inline-flex items-center gap-1.5 rounded-full border border-border/50 bg-muted/20 px-3 py-1 text-xs font-medium"
                    >
                        <span className="text-muted-foreground"><SvgLanguage /></span>
                        {lang}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ── Main component ─────────────────────────────────────────────────────────

export function AttributesCard({ attributes = {}, languages = [] }: Props) {
    const { t } = useTranslation('common');

    const stats = STAT_KEYS
        .filter((k) => k in attributes)
        .map((k) => ({ key: k, attr: attributes[k] }));

    const appearanceChips = APPEARANCE_KEYS
        .filter((k) => k in attributes)
        .map((k) => ({ key: k, attr: attributes[k] }));

    const hasAnyBreast = ['breast_band', 'breast_cup', 'breast_size', 'breast_type', 'breast_presence'].some((k) => k in attributes);

    // Figure chips: body_type, hips, shoulders only (breast handled separately)
    const figureChips = FIGURE_KEYS
        .filter((k) => !BREAST_MERGED.has(k) && k in attributes)
        .map((k) => ({ key: k, attr: attributes[k] }));

    const intimateChips = INTIMATE_KEYS
        .filter((k) => k in attributes)
        .map((k) => ({ key: k, attr: attributes[k] }));

    const personalChips = PERSONAL_KEYS
        .filter((k) => k in attributes)
        .map((k) => ({ key: k, attr: attributes[k] }));

    const knownKeys = new Set<string>([
        ...STAT_KEYS,
        ...APPEARANCE_KEYS,
        ...FIGURE_KEYS,
        ...INTIMATE_KEYS,
        ...PERSONAL_KEYS,
        ...BREAST_MERGED,
    ]);
    const otherChips = Object.entries(attributes)
        .filter(([k]) => !knownKeys.has(k))
        .map(([k, attr]) => ({ key: k, attr }));

    const hasAppearance = appearanceChips.length > 0;
    const hasFigure     = hasAnyBreast || figureChips.length > 0;
    const hasIntimate   = intimateChips.length > 0;
    const hasPersonal   = personalChips.length > 0;
    const hasOther      = otherChips.length > 0;
    const hasLanguages  = languages.length > 0;
    const isEmpty       = stats.length === 0 && !hasAppearance && !hasFigure && !hasIntimate && !hasPersonal && !hasOther && !hasLanguages;

    if (isEmpty) return null;

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle>{t('attributes.title', 'Details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
                <StatStrip stats={stats} />

                {hasAppearance && (
                    <div>
                        <SectionHeading>Appearance</SectionHeading>
                        <ChipGrid>
                            {appearanceChips.map(({ key, attr }) => (
                                <AttrChip key={key} attrKey={key} attr={attr} />
                            ))}
                        </ChipGrid>
                    </div>
                )}

                {hasFigure && (
                    <div>
                        <SectionHeading>Figure</SectionHeading>
                        <ChipGrid>
                            {hasAnyBreast && <BreastChip attrs={attributes} />}
                            {figureChips.map(({ key, attr }) => (
                                <AttrChip key={key} attrKey={key} attr={attr} />
                            ))}
                        </ChipGrid>
                    </div>
                )}

                {hasIntimate && (
                    <div>
                        <SectionHeading>Intimate</SectionHeading>
                        <ChipGrid>
                            {intimateChips.map(({ key, attr }) => (
                                <AttrChip key={key} attrKey={key} attr={attr} />
                            ))}
                        </ChipGrid>
                    </div>
                )}

                {hasPersonal && (
                    <div>
                        <SectionHeading>Personal</SectionHeading>
                        <ChipGrid>
                            {personalChips.map(({ key, attr }) => (
                                <AttrChip key={key} attrKey={key} attr={attr} />
                            ))}
                        </ChipGrid>
                    </div>
                )}

                {hasOther && (
                    <div>
                        <SectionHeading>More</SectionHeading>
                        <ChipGrid>
                            {otherChips.map(({ key, attr }) => (
                                <AttrChip key={key} attrKey={key} attr={attr} />
                            ))}
                        </ChipGrid>
                    </div>
                )}

                {hasLanguages && <LanguageChips languages={languages} />}
            </CardContent>
        </Card>
    );
}
